"use client";

import { useEffect, useRef, useState } from "react";

import { getCurrencyFromLocale, toSnake } from "@repo/locale";
import { flushSync } from "react-dom";

import { changeLocaleAndSelectedCountry } from "@repo/ui/actions/locale";
import { InsuranceCultureChangeModal, useGlobalLoading } from "@repo/ui/components/common";
import { useValidateCurrencyRedirect } from "@repo/ui/Hooks/useValidateCurrencyRedirect";
import { getTabSessionId } from "@repo/ui/lib/tabSessionId";
import type { CountryDropdown, CultureNames } from "@repo/ui/types/cms";

import { useAppDispatch, useAppSelector } from "@repo/store/hooks";
import { clearInsuranceState, selectLocale, useInsuranceState } from "@repo/store/slices";

import CountryOption from "./CountryOption";

export interface CountrySelectorClientProps {
  countryDropdown: CountryDropdown[];
  selectedCountry: CountryDropdown | null;
  cultureNames: CultureNames;
  onSelect?: () => void;
}
const INSURANCE_ACTIVE_MARKETS = ["CL", "CO", "PE"];
export default function CountrySelectorClient({
  countryDropdown,
  selectedCountry,
  cultureNames,
  onSelect,
}: CountrySelectorClientProps) {
  const { beginNavigation, endNavigation } = useGlobalLoading();
  const [open, setOpen] = useState(false);

  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [pendingCountry, setPendingCountry] = useState<string | null>(null);
  const [currentSelectedCountry, setCurrentSelectedCountry] = useState<CountryDropdown | null>(
    selectedCountry,
  );

  const dispatch = useAppDispatch();
  const insuranceData = useInsuranceState();
  const sessionId = getTabSessionId();
  const currentSelectedLocale = useAppSelector(selectLocale);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { validate } = useValidateCurrencyRedirect();

  // Keep the trigger label in sync when the locale changes under us — a locale-carrying
  // page load seeds Redux from the URL via PathLocaleSync. Display only: persisting the
  // country belongs to whoever changed the locale.
  //
  // This used to also fire setSelectedCountry(), a THIRD PUT /v1/session/preferences per
  // country change. validate() dispatches setLocale synchronously, which trips this effect
  // while the router transition is still pending, so the navigation could not commit until
  // that write returned — 1355ms of the post-loader gap, measured. It was redundant in
  // every path that reaches here: the picker persists locale + country itself,
  // PathLocaleSync only mirrors a URL the server already resolved, and
  // hydrateLocaleFromSession reads FROM the session (and only when the path has no locale).
  useEffect(() => {
    if (!currentSelectedLocale) return;

    const selectedCountry = countryDropdown?.find(
      (country) => toSnake(country.localeCode) === toSnake(currentSelectedLocale),
    );

    if (
      selectedCountry &&
      toSnake(selectedCountry.localeCode) !== toSnake(currentSelectedCountry?.localeCode ?? "")
    ) {
      setCurrentSelectedCountry(selectedCountry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedLocale]);

  const proceedWithCountryChange = async (value: string) => {
    setOpen(false);
    onSelect?.();

    // Commit the overlay BEFORE the server action is invoked. Calling a server action in
    // the same tick as this update entangles it with the action's internal transition, so
    // the overlay did not paint until the action's response came back — ~1s of no feedback
    // after the click, measured. A standalone raise paints in ~11ms.
    //
    // beginNavigation, not setIsPageRefreshing: this overlay has to outlive the router.push
    // below, and `isPageRefreshing` is cleared by whichever other component finishes its
    // own work first (Flights.tsx does exactly that when resetApp() re-runs its
    // availability effect).
    flushSync(() => {
      beginNavigation();
    });

    try {
      const oldCurrency = getCurrencyFromLocale(currentSelectedLocale);

      const selectedCountryData = countryDropdown?.find(
        (country) => toSnake(country.localeCode) === toSnake(value),
      );

      // Persist the new locale AND the picked country in a single write. This used to be
      // changeLocaleWithCurrency() followed by setSelectedCountry(), two sequential PUTs
      // to the same endpoint where the second only fixed the `selected_country` the first
      // wrote from the stale session value.
      //
      // ⚠️ Order matters: this await MUST stay BEFORE validate(). validate() starts a
      // router.push to the new /{country}/{lang} prefix, and Next aborts in-flight
      // requests belonging to the route it is leaving. Awaiting this server action
      // after that point left the promise unsettled, so the finally below never ran
      // and the global loader stayed on screen for good (fixed in 8492f273e). Which is
      // also why collapsing the two writes into one shortens the pre-navigation wait
      // instead of just moving it.
      const result = await changeLocaleAndSelectedCountry(
        value,
        selectedCountryData ?? null,
        sessionId,
      );

      if (!result?.success) {
        endNavigation();
        return;
      }

      // validate the redirection flow, if culture change in booking flow need to redirect accordingly
      //
      // The hold is deliberately NOT released after this point. validate() starts a
      // router.push whose RSC transition commits seconds later, and it cannot be awaited,
      // so releasing here left the overlay gone while the old page was still on screen.
      // LoadingProvider releases it when the new pathname lands (and has a hard ceiling in
      // case a navigation never does). The currency-incompatible branch of validate() ends
      // in a window.location assignment instead, which tears down the document — the hold
      // correctly covers the persistor.flush() wait before it.
      validate({ locale: value, oldCurrency });
    } catch (error) {
      console.error("[CountrySelector] Country change error:", error);
      endNavigation();
    }
  };

  const handleValueChange = async (value: string) => {
    // Extract country code from locale (e.g., "es-CL" -> "CL", "es_CO" -> "CO")
    const targetCountryCode = value.split(/[-_]/)[1]?.toUpperCase();
    const isTargetActive = INSURANCE_ACTIVE_MARKETS.includes(targetCountryCode ?? "");

    // Only show modal if:
    // 1. Insurance is selected
    // 2. Target market is NOT in the active list (e.g. going form Chile -> USA)
    if (insuranceData?.isSelected && !isTargetActive) {
      setPendingCountry(value);
      setShowInsuranceModal(true);
      return;
    }

    await proceedWithCountryChange(value);
  };

  const handleInsuranceModalConfirm = async () => {
    dispatch(clearInsuranceState());
    setShowInsuranceModal(false);
    if (pendingCountry) {
      await proceedWithCountryChange(pendingCountry);
      setPendingCountry(null);
    }
  };

  const handleInsuranceModalCancel = () => {
    setShowInsuranceModal(false);
    setPendingCountry(null);
  };

  const handlePointerOver = () => {
    const isHoverSupported =
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(hover: hover)").matches
        : true;
    if (isHoverSupported) {
      setOpen(true);
    }
  };

  useEffect(() => {
    if (!open) return;

    const pointInRect = (rect: DOMRect, x: number, y: number) =>
      x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

    let raf = 0;
    let attached = false;
    let closeTimeout: NodeJS.Timeout | null = null;

    const attach = () => {
      if (!contentRef.current) {
        raf = window.requestAnimationFrame(attach);
        return;
      }
      const onDocPointerMove = (e: PointerEvent) => {
        const x = e.clientX;
        const y = e.clientY;
        const triggerEl = triggerRef.current;
        const contentEl = contentRef.current;
        if (!triggerEl || !contentEl) return;

        const tRect = triggerEl.getBoundingClientRect();
        const cRect = contentEl.getBoundingClientRect();
        const overTrigger = pointInRect(tRect, x, y);
        const overContent = pointInRect(cRect, x, y);

        if (overTrigger || overContent) {
          if (closeTimeout) {
            clearTimeout(closeTimeout);
            closeTimeout = null;
          }
        } else {
          if (!closeTimeout) {
            closeTimeout = setTimeout(() => {
              setOpen(false);
              closeTimeout = null;
            }, 150); //smooth transition
          }
        }
      };
      const onDocClickOutside = (e: MouseEvent) => {
        const triggerEl = triggerRef.current;
        const contentEl = contentRef.current;
        if (!triggerEl || !contentEl) return;

        const target = e.target as Node;
        if (!triggerEl.contains(target) && !contentEl.contains(target)) {
          if (closeTimeout) {
            clearTimeout(closeTimeout);
            closeTimeout = null;
          }
          setOpen(false);
        }
      };
      const isHoverSupported =
        typeof window !== "undefined" && typeof window.matchMedia === "function"
          ? window.matchMedia("(hover: hover)").matches
          : true;
      if (isHoverSupported) {
        document.addEventListener("pointermove", onDocPointerMove);
      }
      document.addEventListener("mousedown", onDocClickOutside);
      attached = true;
      cleanup = () => {
        if (isHoverSupported) {
          document.removeEventListener("pointermove", onDocPointerMove);
        }
        document.removeEventListener("mousedown", onDocClickOutside);
        if (closeTimeout) {
          clearTimeout(closeTimeout);
          closeTimeout = null;
        }
      };
    };

    let cleanup = () => {};
    raf = window.requestAnimationFrame(attach);
    return () => {
      window.cancelAnimationFrame(raf);
      if (attached) cleanup();
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
    };
  }, [open]);

  return (
    <>
      <div className="relative z-[999]">
        <button
          ref={triggerRef}
          type="button"
          className="text-brand-blue w-full cursor-pointer border-transparent bg-transparent p-0 shadow-none md:w-auto focus-visible:outline-none"
          onPointerOver={handlePointerOver}
          onMouseEnter={handlePointerOver}
          onClick={(e) => {
            e.preventDefault();
            setOpen((prev) => !prev);
          }}
          disabled={!countryDropdown || countryDropdown.length === 0}
          data-testid="country-selector-trigger-btn"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span
            className="border-0 bg-transparent p-0 cursor-pointer"
            data-testid="country-selector-selected-lbl"
          >
            {currentSelectedCountry ? (
              <CountryOption
                culture={currentSelectedCountry}
                cultureNames={cultureNames}
                selectedCountry={currentSelectedCountry}
                testidPrefix="selected"
              />
            ) : (
              "Select country"
            )}
          </span>
        </button>

        {open && (
          <div
            ref={contentRef}
            role="listbox"
            tabIndex={-1}
            data-testid="country-selector-content-section"
            onPointerOver={handlePointerOver}
            onMouseEnter={handlePointerOver}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            className="absolute right-0 top-full mt-1 w-full md:w-55 rounded-md border bg-white shadow-md z-250"
          >
            <ul className="p-1 max-h-[320px] overflow-y-auto" data-testid="country-selector-list">
              {countryDropdown?.map((country: CountryDropdown) => (
                <li key={country.localeCode} data-testid="country-selector-option">
                  <button
                    type="button"
                    role="option"
                    aria-selected={country.localeCode === currentSelectedCountry?.localeCode}
                    className="w-full text-left rounded-sm px-2 py-1.5 hover:bg-brand-gray-light cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleValueChange(country.localeCode);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    data-testid="country-selector-option-btn"
                  >
                    <CountryOption
                      culture={country}
                      cultureNames={cultureNames}
                      selectedCountry={currentSelectedCountry}
                    />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <InsuranceCultureChangeModal
        open={showInsuranceModal}
        onOpenChange={setShowInsuranceModal}
        onConfirm={handleInsuranceModalConfirm}
        onCancel={handleInsuranceModalCancel}
      />
    </>
  );
}
